import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MSME_WORKSPACE_UPDATED_EVENT } from '../constants/workspaceEvents';
import { invalidateWorkspaceQueries } from './useOrchestrationEvents';
import { useMsmeProfileQuery, useMsmeStatsQuery } from './queries/useMsmeWorkspaceQueries';
import { hasCompletedWorkflowPlanner } from '../utils/workflowCompletion';
import { reconcileHeroCarbonTotalKg } from '../utils/reconcileHeroCarbonTotalKg';

const DISMISSED_NOTIFICATIONS_KEY = 'ci-header-notifications-dismissed';

export interface HeaderNotification {
  id: string;
  title: string;
  description: string;
  path: string;
  severity: 'info' | 'warning';
}

export interface HeaderWorkspaceSnapshot {
  isLoading: boolean;
  apiOnline: boolean | null;
  carbonScore: number | null;
  totalTCO2e: number | null;
  lastAssessmentLabel: string | null;
  setupIncomplete: boolean;
  notifications: HeaderNotification[];
  unreadCount: number;
}

interface WorkspaceSignals {
  apiOnline: boolean | null;
  carbonScore: number | null;
  totalTCO2e: number | null;
  lastAssessment: string | null;
  setupIncomplete: boolean;
}

const readDismissedIds = (): Set<string> => {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const raw = sessionStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw) as string[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const persistDismissedIds = (ids: Set<string>) => {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(DISMISSED_NOTIFICATIONS_KEY, JSON.stringify(Array.from(ids)));
};

const resolveHeaderCarbonScore = (
  statsScore: unknown,
  profileScore: unknown
): number | null => {
  const fromStats = Number(statsScore);
  if (Number.isFinite(fromStats) && fromStats > 0) {
    return fromStats;
  }

  const fromProfile = Number(profileScore);
  if (Number.isFinite(fromProfile) && fromProfile > 0) {
    return fromProfile;
  }

  return null;
};

const buildNotifications = (input: {
  setupIncomplete: boolean;
  carbonScore: number | null;
  lastAssessment: string | null;
  apiOnline: boolean | null;
  totalTCO2e: number | null;
}): HeaderNotification[] => {
  const items: HeaderNotification[] = [];

  if (input.apiOnline === false) {
    items.push({
      id: 'api-offline',
      title: 'Workspace connection unavailable',
      description: 'Carbon data may be stale until the API is reachable again.',
      path: '/dashboard',
      severity: 'warning',
    });
  }

  // DEEP-BUG-061: if Home already shows a footprint total, do not nag for "first assessment".
  const hasFootprint = Number(input.totalTCO2e) > 0;
  if (input.setupIncomplete && !hasFootprint) {
    items.push({
      id: 'setup-incomplete',
      title: 'Complete operations setup',
      description: 'Map manufacturing processes so assessments use accurate activity drivers.',
      path: '/operations',
      severity: 'warning',
    });
  }

  if (!input.lastAssessment && !hasFootprint) {
    items.push({
      id: 'no-assessment',
      title: 'Run your first carbon assessment',
      description: 'Upload activity data or documents to generate footprint totals.',
      path: '/carbon-footprint?section=start-assessment',
      severity: 'info',
    });
  } else if (input.carbonScore !== null && input.carbonScore < 50) {
    items.push({
      id: 'low-score',
      title: 'Carbon score needs attention',
      description: 'Review hotspots and reduction targets to improve your score.',
      path: '/emissions-reduction-targets',
      severity: 'warning',
    });
  }

  const lastMs = input.lastAssessment ? Date.parse(input.lastAssessment) : NaN;
  if (Number.isFinite(lastMs)) {
    const daysSince = (Date.now() - lastMs) / (1000 * 60 * 60 * 24);
    if (daysSince > 30) {
      items.push({
        id: 'stale-assessment',
        title: 'Refresh your carbon assessment',
        description: `Last assessment was ${Math.floor(daysSince)} days ago.`,
        path: '/carbon-footprint?section=document-assessment',
        severity: 'info',
      });
    }
  }

  return items;
};

const applyDismissedFilter = (
  signals: WorkspaceSignals,
  dismissed: Set<string>
): HeaderWorkspaceSnapshot => {
  const allNotifications = buildNotifications({
    setupIncomplete: signals.setupIncomplete,
    carbonScore: signals.carbonScore,
    lastAssessment: signals.lastAssessment,
    apiOnline: signals.apiOnline,
    totalTCO2e: signals.totalTCO2e,
  });
  const visible = allNotifications.filter((item) => !dismissed.has(item.id));

  return {
    isLoading: false,
    apiOnline: signals.apiOnline,
    carbonScore: signals.carbonScore,
    totalTCO2e: signals.totalTCO2e,
    lastAssessmentLabel: signals.lastAssessment
      ? new Date(signals.lastAssessment).toLocaleDateString()
      : null,
    setupIncomplete: signals.setupIncomplete,
    notifications: visible,
    unreadCount: visible.length,
  };
};

const deriveWorkspaceSignals = (
  statsResponse: unknown,
  profileResponse: unknown
): WorkspaceSignals => {
  const apiOnline = statsResponse !== null || profileResponse !== null;

  const statsData = statsResponse as { data?: { carbon?: Record<string, unknown> } } | null;
  const profileData = profileResponse as {
    data?: { carbonScore?: number; business?: { manufacturingWorkflow?: unknown } };
  } | null;

  const carbon = statsData?.data?.carbon;
  const emissionsKg = Number(carbon?.totalCO2Emissions);
  const esg = carbon?.esgScopes as
    | {
      scope1?: { total?: number } | number;
      scope2?: { total?: number } | number;
      scope3?: { total?: number } | number;
    }
    | undefined;
  const scopeSumKg = (
    Number((esg?.scope1 as { total?: number } | undefined)?.total ?? esg?.scope1)
    || 0
  ) + (
    Number((esg?.scope2 as { total?: number } | undefined)?.total ?? esg?.scope2)
    || 0
  ) + (
    Number((esg?.scope3 as { total?: number } | undefined)?.total ?? esg?.scope3)
    || 0
  );
  // BUG-025: match Home — prefer scope-coherent total over a divergent assessment dump.
  const reconciledKg = reconcileHeroCarbonTotalKg(
    Number.isFinite(emissionsKg) && emissionsKg > 0 ? emissionsKg : 0,
    scopeSumKg
  );
  const resolvedEmissionsKg = reconciledKg > 0 ? reconciledKg : 0;

  // Prefer stats score (canonical inventory). Stale profile.carbonScore caused BUG-030/058.
  const carbonScore = resolveHeaderCarbonScore(carbon?.currentScore, null);
  const totalTCO2e = resolvedEmissionsKg > 0 ? resolvedEmissionsKg / 1000 : null;
  const lastAssessment =
    typeof carbon?.lastAssessment === 'string' ? carbon.lastAssessment : null;
  const workflow = profileData?.data?.business?.manufacturingWorkflow;
  const setupIncomplete = !hasCompletedWorkflowPlanner(workflow);

  return {
    apiOnline,
    carbonScore,
    totalTCO2e,
    lastAssessment,
    setupIncomplete,
  };
};

export function useHeaderWorkspace(enabled: boolean) {
  const queryClient = useQueryClient();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readDismissedIds());

  const statsQuery = useMsmeStatsQuery(enabled);
  const profileQuery = useMsmeProfileQuery(enabled);

  const dismissNotification = useCallback((id: string) => {
    setDismissedIds((current) => {
      if (current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.add(id);
      persistDismissedIds(next);
      return next;
    });
  }, []);

  const refreshWorkspace = useCallback(() => {
    invalidateWorkspaceQueries(queryClient);
  }, [queryClient]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    const handleWorkspaceUpdated = () => {
      refreshWorkspace();
    };

    window.addEventListener(MSME_WORKSPACE_UPDATED_EVENT, handleWorkspaceUpdated);
    return () => {
      window.removeEventListener(MSME_WORKSPACE_UPDATED_EVENT, handleWorkspaceUpdated);
    };
  }, [enabled, refreshWorkspace]);

  const refreshNotifications = useCallback(() => {
    setDismissedIds(readDismissedIds());
  }, []);

  const signals = useMemo(() => {
    if (!enabled) {
      return null;
    }
    if (!statsQuery.isFetched && !profileQuery.isFetched) {
      return null;
    }
    return deriveWorkspaceSignals(statsQuery.data ?? null, profileQuery.data ?? null);
  }, [enabled, statsQuery.data, statsQuery.isFetched, profileQuery.data, profileQuery.isFetched]);

  const isLoading = enabled && (statsQuery.isLoading || profileQuery.isLoading);

  return useMemo(() => {
    if (!enabled || !signals) {
      return {
        isLoading,
        apiOnline: null,
        carbonScore: null,
        totalTCO2e: null,
        lastAssessmentLabel: null,
        setupIncomplete: false,
        notifications: [],
        unreadCount: 0,
        dismissNotification,
        refreshNotifications,
        refreshWorkspace,
      };
    }

    return {
      ...applyDismissedFilter(signals, dismissedIds),
      dismissNotification,
      refreshNotifications,
      refreshWorkspace,
    };
  }, [
    enabled,
    signals,
    isLoading,
    dismissedIds,
    dismissNotification,
    refreshNotifications,
    refreshWorkspace,
  ]);
}
